import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import classnames from 'classnames/bind';
import classNames from 'classnames';
import { useIntl } from 'umi';
import { Popover, Space } from 'antd';
import { Icon, Tooltip, Button, message } from '@/components';
import { disableWhiteboard } from '@/api';
import { WhiteboardTool, WhiteboardAuth, WhiteboardInstance, DrawPlugin } from '@/types/whiteboard';
import { meeting } from '@/models';
import * as QtBridge from '@/utils/Bridge';
import Toolbar, { brushTool, shapeTool, selectTool, textTool } from './Tool/Toolbar';
import { mapWhiteboard2Toolbar, mapToolbar2Whiteboard } from './adapter';
import Style from './index.less';
import { eraserTool } from './Tool/EraserTool';
import { elkLog } from '@/utils/elkReport';

const $c = classnames.bind(Style);

const DEFAULT_STROKE_WIDTH = 5;

const DEFAULT_SELECT_COLOR = '#018df5';

WhiteBoardSDK.interceptShowToast(() => {
  return false;
});

const Whiteboard = React.memo<
  {
    channelId: number;
    whiteboardUid: string;
    uid: string;
    uname: string;
    yxuid: string;
    willClose: boolean;
    className?: string;
  } & WhiteboardAuth
>(
  ({
    whiteboardChannelId,
    className,
    channelId,
    whiteboardUid,
    uid,
    uname,
    yxuid,
    appkey,
    nonce,
    curTime,
    checksum,
    willClose,
    locale,
    exportToZh,
    exportToEn,
    exportToJa,
    exportable = true,
    ...otherProps
  }) => {
    const { formatMessage } = useIntl();

    const containerRef = useRef<HTMLDivElement>(null);
    const whiteboardInstance = useRef<WhiteboardInstance>();
    const drawPlugin = useRef<DrawPlugin>();
    const history = useRef<WhiteboardTool[]>([]);
    const [state, setState] = useState<'none' | 'initializing' | 'done'>('none');
    const [
      visibleYouAreCurrentlyInPerspectiveSynchronization,
      setVisibleYouAreCurrentlyInPerspectiveSynchronization,
    ] = useState(false);
    const [visibleModalClose, setVisibleModalClose] = useState(false);

    const [tool, reactSetTool] = useState(WhiteboardTool.PEN);

    const exportToThird = useMemo(() => {
      if (locale?.includes('zh')) {
        return exportToZh;
      } else if (locale?.includes('en')) {
        return exportToEn;
      } else {
        return exportToJa;
      }
    }, [exportToZh, exportToEn, exportToJa, locale]);

    const setTool = useCallback((t: WhiteboardTool) => {
      const toolbarTool = mapWhiteboard2Toolbar(t);
      if (toolbarTool === brushTool) {
        drawPlugin.current?.setColor(brushTool.color);
        drawPlugin.current?.setStrokeWidth(brushTool.strokewidth);
        drawPlugin.current?.setOpacity(brushTool.opacity);
      } else {
        drawPlugin.current?.setStrokeWidth(DEFAULT_STROKE_WIDTH);
        drawPlugin.current?.setOpacity(1);
        if (toolbarTool === shapeTool) {
          drawPlugin.current?.setColor(shapeTool.color);
        } else if (
          toolbarTool === selectTool ||
          toolbarTool === textTool ||
          toolbarTool === eraserTool
        ) {
          drawPlugin.current?.setColor(DEFAULT_SELECT_COLOR);
        }
      }
      drawPlugin.current?.setTool(t);
      reactSetTool(t);
    }, []);

    const [zoomFactor, setZoomFactor] = useState(1);

    const [perspectiveSynchronizationMember, setPerspectiveSynchronizationMember] = useState<{
      avatarUrl: string;
      name: string;
    }>();
    const [perspectiveSynchronizationYxuid, reactSetPerspectiveSynchronizationYxuid] = useState('');
    const perspectiveSynchronizationYxuidRef = useRef(perspectiveSynchronizationYxuid);
    const setPerspectiveSynchronizationYxuid = useCallback((v: string) => {
      perspectiveSynchronizationYxuidRef.current = v;
      reactSetPerspectiveSynchronizationYxuid(v);
      if (!v) {
        setPerspectiveSynchronizationMember(undefined);
        // 获取视角同步人的信息
      } else if (window.isPC || window.isPad) {
        QtBridge.getPeopleInfo({ popoid: '', yxuid: Number.parseInt(v, 10) })
          .then((res) => {
            if (perspectiveSynchronizationYxuidRef.current === v) {
              setPerspectiveSynchronizationMember({
                avatarUrl: res.avatarUrl,
                name: res.name,
              });
            }
          })
          .catch((e) => {
            console.error('从客户端获取云信用户信息失败. error=%o', e);
          });
      } else {
        const member = meeting.getMemberByYxuid(v);
        if (member) {
          setPerspectiveSynchronizationMember({
            avatarUrl: member.memberAvatarUrl,
            name: member.memberName,
          });
        }
      }
    }, []);

    const clear = useCallback(() => {
      drawPlugin.current?.unsetSelfAsBroadcaster();
      whiteboardInstance.current?.destroy();
      whiteboardInstance.current = undefined;
      meeting.setWhiteboardUid('', undefined, '');
      if (window.isPC) {
        QtBridge.closeView();
      }
    }, []);

    const destroy = useCallback(() => {
      (window.isPC ? Promise.resolve() : disableWhiteboard(channelId))
        .then(() => {
          clear();
        })
        .catch((e) => {
          console.error('failed to disable whiteboard', e);
          message.error({ content: e.message || String(e) });
        });
    }, [channelId, clear]);

    const exit = useCallback(() => {
      if (drawPlugin.current?.isClearAvailable()) {
        setVisibleModalClose(true);
      } else {
        destroy();
      }
    }, [destroy]);

    const exportToLocal = useCallback(() => {
      if (!window.isPC) {
        return;
      }
      const res = drawPlugin.current!.exportAsBase64String({
        content: 'fitToContent',
        type: 'png',
      });
      QtBridge.saveImage({
        operateType: QtBridge.SAVE_IMAGE_OPERATE_TYPE.WHOLE,
        url: res ? res.content.replace(/data:image\/png;base64,/i, '') : '',
      });
    }, []);

    const exportToCloud = useCallback(() => {
      if (!window.isPC) {
        return;
      }
      const res = drawPlugin.current!.exportAsBase64String({
        content: 'fitToContent',
        type: 'png',
      });
      QtBridge.uploadSnapshot({
        operateType: QtBridge.SAVE_IMAGE_OPERATE_TYPE.WHOLE,
        ImageType: '.png',
        base64Image: res ? res.content.replace(/data:image\/png;base64,/i, '') : '',
      });
    }, []);

    const exitWithExport = useCallback(() => {
      if (!window.isPC) {
        return;
      }
      exportToCloud();
      destroy();
    }, [destroy, exportToCloud]);

    const snapshot = useCallback(() => {
      if (!window.isPC) {
        return;
      }

      const res = drawPlugin.current!.exportAsBase64String({
        content: 'clip',
        type: 'png',
      });
      QtBridge.uploadSnapshot({
        operateType: QtBridge.SAVE_IMAGE_OPERATE_TYPE.CLIP,
        ImageType: '.png',
        base64Image: res ? res.content.replace(/data:image\/png;base64,/i, '') : '',
      });
    }, []);

    // 白板回调函数只注册一次，因此不能依赖状态变更，所需的状态应为 ref
    const onWhiteboardStateChange = useCallback(
      (stateName: string, infos: any) => {
        if (stateName === 'vision') {
          const data = infos as {
            broadcaster: undefined | string;
            broadcasterName: undefined | string;
            mode: 'broadcaster' | 'follower' | 'freeObserver';
          };
          setPerspectiveSynchronizationYxuid(data.broadcaster || '');
          if (data.mode === 'follower') {
            if (
              perspectiveSynchronizationYxuidRef.current &&
              perspectiveSynchronizationYxuidRef.current !== yxuid
            ) {
              setVisibleYouAreCurrentlyInPerspectiveSynchronization(true);
            }
          } else if (data.mode === 'broadcaster') {
            if (perspectiveSynchronizationYxuidRef.current === yxuid) {
              message.info({
                content: formatMessage({
                  id: 'THE_PERSPECTIVE_SYNCHRONIZATION_HAS_BEEN_TURNED_ON',
                }),
                noIcon: true,
                duration: 2,
              });
            }
          } else if (data.mode === 'freeObserver') {
            setVisibleYouAreCurrentlyInPerspectiveSynchronization(false);
          }
        } else if (stateName === 'zoomFactor') {
          const scale = infos as number;
          setZoomFactor(scale);
        } else if (stateName === 'currTool') {
          const currTool = infos as string;
          history.current.push(currTool as WhiteboardTool);
          setTool(currTool as WhiteboardTool);
        }
      },
      [formatMessage, setPerspectiveSynchronizationYxuid, yxuid, setTool]
    );

    const onToolbarLeftChange = useCallback<Parameters<typeof Toolbar>[0]['onChange']>(
      (nextTool) => {
        setTool(mapToolbar2Whiteboard(nextTool));
      },
      [setTool]
    );

    const onBeforeUnload = useCallback<OnBeforeUnloadEventHandlerNonNull>((e) => {
      e.preventDefault();
      e.returnValue = true;
      return null;
    }, []);

    const ToolbarLeftTop = useMemo(() => {
      if (whiteboardUid === uid || window.isPC) {
        return (
          <div className={$c('toolbar-left-top')}>
            {whiteboardUid === uid && window.isPC && (
              <div
                className={$c('toolbar-close', { 'no-right': !window.isPC })}
                onClick={() => {
                  if (window.isPC) {
                    exit();
                  } else {
                    destroy();
                  }
                }}
              >
                {formatMessage({ id: 'TERMINATE' })}
              </div>
            )}
            {window.isPC && (
              <>
                <Tooltip
                  className="BOpkGFsjP8"
                  title={formatMessage({ id: 'exportTo' }, { name: exportToThird })}
                  placement="bottom"
                >
                  <Icon
                    className={$c('icon')}
                    name="icon-message_Upload"
                    onClick={() => {
                      QtBridge.buriedPoint({
                        key: 'whiteboard_save',
                        value: 'whiteboard_drivesave',
                      });
                      exportToCloud();
                    }}
                  />
                </Tooltip>
                <Popover
                  trigger="click"
                  placement="bottomRight"
                  overlayClassName={classNames(
                    'BOpkGFsjP8',
                    exportable ? '' : 'dropdown-container'
                  )}
                  align={{
                    offset: [36, 4],
                  }}
                  content={
                    <>
                      {exportable && (
                        <div
                          className="menu-item"
                          onClick={() => {
                            QtBridge.buriedPoint({
                              key: 'whiteboard_save',
                              value: 'whiteboard_drivesave',
                            });
                            exportToCloud();
                          }}
                        >
                          <Icon name="icon-message_Upload" />
                          <span className="menu-text">
                            {formatMessage({ id: 'exportTo' }, { name: exportToThird })}
                          </span>
                        </div>
                      )}

                      <div
                        className="menu-item"
                        onClick={() => {
                          QtBridge.buriedPoint({
                            key: 'whiteboard_save',
                            value: 'whiteboard_localsave',
                          });
                          exportToLocal();
                        }}
                      >
                        <Icon name="icon-download" />
                        <span className="menu-text">
                          {formatMessage({ id: 'EXPORT_TO_LOCAL' })}
                        </span>
                      </div>
                    </>
                  }
                >
                  <div className={$c('icon-arrowdown-container')}>
                    <Icon className={$c('icon-arrowdown')} name="icon-jiantou" />
                  </div>
                </Popover>
                {exportable && (
                  <Tooltip
                    className="BOpkGFsjP8"
                    title={formatMessage({ id: 'SNAPSHOT' })}
                    placement="bottom"
                  >
                    <Icon
                      className={$c('icon')}
                      name="icon-a-Screenshot"
                      onClick={() => {
                        QtBridge.buriedPoint({
                          key: 'whiteboard_save',
                          value: 'whiteboard_quicksave',
                        });
                        snapshot();
                      }}
                      style={{ marginLeft: 8, marginRight: 4 }}
                    />
                  </Tooltip>
                )}
              </>
            )}
          </div>
        );
      }
      return null;
    }, [destroy, exit, exportToLocal, exportToCloud, whiteboardUid, snapshot, uid, formatMessage]);

    const ZoomTool = useMemo(() => {
      return (
        <div className={$c('zoom')}>
          <Tooltip
            className="BOpkGFsjP8"
            title={formatMessage({ id: 'SCALE_TO_PROPER_SIZE' })}
            placement="bottom"
          >
            <Icon
              className={$c('icon')}
              name="icon-adaptive"
              onClick={() => {
                drawPlugin.current?.fitToContent();
              }}
            />
          </Tooltip>
          <Tooltip
            className="BOpkGFsjP8"
            title={formatMessage({ id: 'ZOOM_OUT' })}
            placement="bottom"
          >
            <Icon
              className={$c('icon')}
              name="icon-reduce"
              onClick={() => {
                drawPlugin.current?.zoomOut();
              }}
            />
          </Tooltip>
          <Tooltip
            className="BOpkGFsjP8"
            title={formatMessage({ id: 'ZOOM_IN' })}
            placement="bottom"
          >
            <Icon
              className={$c('icon')}
              name="icon-add"
              onClick={() => {
                drawPlugin.current?.zoomIn();
              }}
            />
          </Tooltip>
          <Tooltip
            className="BOpkGFsjP8"
            title={formatMessage({ id: 'ZOOM_TO_100' })}
            placement="bottom"
          >
            <span
              className={$c('percent-100')}
              onClick={() => {
                drawPlugin.current?.zoomTo(1);
              }}
            >
              {(zoomFactor * 100).toFixed(0)}%
            </span>
          </Tooltip>
        </div>
      );
    }, [formatMessage, zoomFactor]);

    const DragTool = useMemo(() => {
      return (
        <Tooltip
          className="BOpkGFsjP8"
          title={formatMessage({ id: 'MOVE_CANVAS' })}
          placement="bottom"
        >
          <Icon
            className={$c('drag-icon', { selected: tool === WhiteboardTool.PAN })}
            name="icon-translation"
            onClick={() => {
              if (tool === WhiteboardTool.PAN) {
                const lastTool = [...history.current].reverse().find((item) => {
                  return item !== WhiteboardTool.PAN;
                });
                setTool(lastTool || WhiteboardTool.SELECT);
              } else {
                setTool(WhiteboardTool.PAN);
              }
            }}
          />
        </Tooltip>
      );
    }, [formatMessage, tool, setTool]);

    const ToolbarRightTop = useMemo(() => {
      return (
        <div className={$c('toolbar-right-top', { syncing: perspectiveSynchronizationYxuid })}>
          {perspectiveSynchronizationYxuid ? (
            <>
              {perspectiveSynchronizationYxuid === yxuid ? (
                <>
                  <div className={$c('perspective-synchronization')}>
                    <Icon
                      className={$c('synchronization-icon')}
                      name="icon-a-synchronizetheperspective"
                    />
                    <span className={$c('synchronization-text')}>
                      {formatMessage({ id: 'PERSPECTIVE_SYNCING' })}
                    </span>
                    <div className={$c('split-line')} />
                    <Tooltip
                      className="BOpkGFsjP8"
                      title={formatMessage({ id: 'TERMINATE' })}
                      placement="bottom"
                    >
                      <Icon
                        className={$c('stop-icon')}
                        name="icon-a-endmeeting_nor"
                        onClick={() => {
                          drawPlugin.current?.unsetSelfAsBroadcaster();
                        }}
                      />
                    </Tooltip>
                  </div>
                  <div className={$c('drag-and-zoom')}>
                    {DragTool}
                    <div className={$c('split-line')} />
                    {ZoomTool}
                  </div>
                </>
              ) : (
                <div className={$c('perspective-synchronization', 'follower')}>
                  <Icon
                    className={$c('synchronization-icon')}
                    name="icon-a-synchronizetheperspective"
                  />
                  <span className={$c('split-dot')} />
                  <span className={$c('broadcaster-name')}>
                    {perspectiveSynchronizationMember?.name || ''}
                  </span>
                  <span className={$c('name-postfix')}>
                    {formatMessage({ id: 'PERSPECTIVE_OF' })}
                  </span>
                </div>
              )}
            </>
          ) : (
            <>
              <div className={$c('perspective-synchronization')}>
                <Tooltip
                  className="BOpkGFsjP8"
                  title={
                    <div>
                      {formatMessage({ id: 'PERSPECTIVE_SYNCHRONIZATION' })}
                      <br />
                      {formatMessage({ id: 'CURRENT_PARTICIPANTS_WILL_FOLLOW_YOUR_PERSPECTIVE' })}
                    </div>
                  }
                  placement="bottom"
                >
                  <Icon
                    className={$c('synchronization-icon')}
                    name="icon-a-synchronizetheperspective"
                    onClick={() => {
                      QtBridge.buriedPoint({
                        key: 'whiteboard_visioncontrol',
                        value: 'visioncontrol_on',
                      });
                      drawPlugin.current?.setSelfAsBroadcaster();
                    }}
                  />
                </Tooltip>
              </div>
              <div className={$c('split-line')} />
              {DragTool}
              <div className={$c('split-line')} />
              {ZoomTool}
            </>
          )}
        </div>
      );
    }, [
      DragTool,
      ZoomTool,
      formatMessage,
      yxuid,
      perspectiveSynchronizationYxuid,
      perspectiveSynchronizationMember,
    ]);

    const ToolbarLeft = useMemo(() => {
      return (
        <div className={$c('toolbar-left')}>
          <Toolbar tool={mapWhiteboard2Toolbar(tool)} onChange={onToolbarLeftChange} />
        </div>
      );
    }, [tool, onToolbarLeftChange]);

    const ModalClose = useMemo(() => {
      if (!visibleModalClose) {
        return null;
      }
      return (
        <div className={$c('close-modal-wrapper')}>
          <div className={$c('close-modal')}>
            <div className={$c('text')}>
              {formatMessage({ id: 'EXPORT_BEFORE_TERMINATE_WHITEBOARD' })}
            </div>
            <Space className={$c('button-list')} size={8}>
              <div className={$c('button')} onClick={destroy}>
                {formatMessage({ id: 'DONT_EXPORT' })}
              </div>
              <div
                className={$c('button')}
                onClick={() => {
                  setVisibleModalClose(false);
                }}
              >
                {formatMessage({ id: 'CANCEL' })}
              </div>
              <div className={$c('button', 'blue')} onClick={exitWithExport}>
                {formatMessage({ id: 'EXPORT' })}
              </div>
            </Space>
          </div>
        </div>
      );
    }, [exitWithExport, formatMessage, visibleModalClose, destroy]);

    const ModalYouAreCurrentlyInPerspectiveSynchronization = useMemo(() => {
      if (!visibleYouAreCurrentlyInPerspectiveSynchronization) {
        return null;
      }
      return (
        <div className={$c('modal-you-are-currently-in-perspective-synchronization')}>
          <div className={$c('modal-container')}>
            <img
              className={$c('avatar')}
              src={perspectiveSynchronizationMember?.avatarUrl}
              alt=""
            />
            <div className={$c('text')}>
              <span className={$c('broadcaster-name')}>
                {perspectiveSynchronizationMember?.name || ''}
              </span>
              <span className={$c('perspective-synchronization-is-turned-on')}>
                {formatMessage({ id: 'PERSPECTIVE_SYNCHRONIZATION_IS_TURNED_ON' })}
              </span>
            </div>
            <div className={$c('text', 'no-margin')}>
              <span>{formatMessage({ id: 'YOU_WILL_FOLLOW' })}</span>
              <span className={$c('broadcaster-name')}>
                {perspectiveSynchronizationMember?.name || ''}
              </span>
              <span className={$c('perspective-of')}>
                {formatMessage({ id: 'PERSPECTIVE_OF' })}
              </span>
            </div>
            <Button
              className={$c('confirm')}
              onClick={() => {
                setVisibleYouAreCurrentlyInPerspectiveSynchronization(false);
              }}
            >
              {formatMessage({ id: 'CONFIRM' })}
            </Button>
          </div>
        </div>
      );
    }, [
      formatMessage,
      visibleYouAreCurrentlyInPerspectiveSynchronization,
      perspectiveSynchronizationMember,
    ]);

    useEffect(() => {
      // setTimeout(() => {
      if (!yxuid || !uname || !nonce || !checksum || !curTime || !appkey || !whiteboardChannelId) {
        return;
      }
      if (whiteboardUid && state === 'none') {
        setState('initializing');
        let platform = 'web';
        if (isiOSApp) {
          platform = 'ios';
        } else if (isAndroidApp) {
          platform = 'android';
        } else if (isMobile) {
          if (/iPhone/.test(window.navigator.userAgent)) {
            platform = 'ios';
          } else if (/Android/.test(window.navigator.userAgent)) {
            platform = 'android';
          }
        }
        whiteboardInstance.current = WhiteBoardSDK.getInstance({
          appKey: appkey,
          uid: yxuid,
          nickname: uname,
          container: containerRef.current,
          platform,
          record: false, // 是否开启录制
          drawPluginParams: {
            disableAutoResize: false,
          },
          getAuthInfo: async () => {
            return {
              nonce,
              checksum,
              curTime,
            };
          },
        });
        whiteboardInstance.current
          ?.joinRoom(
            {
              channel: whiteboardChannelId.toString(),
              createRoom: true, // 云信建议这里设置 true
            },
            {
              onconnected: () => {
                console.log('whiteboard connected, cid=%s, uid=%s', channelId, yxuid);
                elkLog('白板连接成功');
              },
              onwillreconnect: () => {
                console.log('whiteboard will reconnect');
                elkLog('白板即将重连');
              },
              ondisconnected: (err: any) => {
                elkLog('白板重连失败', err);
                console.log('whiteboard on disconnected', err);
              },
            }
          )
          .then((plugin) => {
            elkLog('白板joinRoom成功');
            drawPlugin.current = plugin;
            plugin.enableDraw(true);
            plugin.setAppConfig({
              showCursorNickname: true,
              showLaserNickname: true,
              showSelectNickname: true,
              canvasBgColor: 'rgb(240, 242, 245)',
              useSoftKeyboard: isPad || false,
            });
            if (!isMobile && !isiOSApp && !isAndroidApp) {
              plugin.setFontFamily({
                fontFamily:
                  'Chinese Quote, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, PingFang SC, Hiragino Sans GB, Microsoft YaHei, Helvetica Neue, Helvetica, Arial, sans-serif',
              });
            }

            // if (whiteboardUid === uid) {
            //   plugin.clear();
            // }
            plugin.zoomTo(1, false);
            plugin.on('event:appState:change', onWhiteboardStateChange);

            let initialTool = WhiteboardTool.SELECT as WhiteboardTool;
            // TODO ipad 只读，后续需要编辑时去除 ipad 判断
            // if (isMobile || isAndroidApp || isiOSApp || isPad) {
            //   initialTool = WhiteboardTool.PAN;
            // }
            if (isMobile || isAndroidApp || isiOSApp) {
              initialTool = WhiteboardTool.PAN;
            }

            setTool(initialTool);
            const visionState = plugin.getVisionState();
            if (visionState.broadcaster) {
              setPerspectiveSynchronizationYxuid(visionState.broadcaster);
              if (visionState.mode === 'follower') {
                setVisibleYouAreCurrentlyInPerspectiveSynchronization(true);
              }
            }

            setState('done');
            meeting.setWhiteboardLoading(false);
            if (whiteboardUid === uid && !window.isPC && !window.isiOSApp && !window.isAndroidApp) {
              window.addEventListener('beforeunload', onBeforeUnload, { capture: true });
            }
          })
          .catch((e) => {
            clear();
            elkLog('白板joinRoom成失败', e);
            console.error('failed to enable whiteboard', e);
            message.error({ content: e.message || String(e) });
            meeting.setWhiteboardLoading(false);
          });
      }
      // }, 100);
    }, [
      formatMessage,
      appkey,
      channelId,
      nonce,
      checksum,
      curTime,
      clear,
      setTool,
      perspectiveSynchronizationYxuid,
      setPerspectiveSynchronizationYxuid,
      uid,
      yxuid,
      uname,
      state,
      whiteboardUid,
      onWhiteboardStateChange,
      onBeforeUnload,
      whiteboardChannelId,
    ]);

    useEffect(() => {
      if (willClose && state === 'done') {
        // TODO: PC 时调用 exit 函数调起对话框
        if (whiteboardUid === uid) {
          destroy();
        } else {
          clear();
        }
      }
    }, [willClose, destroy, clear, state, whiteboardUid, uid]);

    useEffect(() => {
      return () => {
        clear();
        window.removeEventListener('beforeunload', onBeforeUnload, { capture: true });
      };
    }, [clear, onBeforeUnload]);

    const domRef = useRef<HTMLDivElement | undefined>();

    useEffect(() => {
      function preDefault(e: TouchEvent) {
        e.preventDefault(); // 阻止默认的处理方式(阻止下拉滑动的效果)
      }
      domRef.current?.addEventListener('touchmove', preDefault, { passive: false });
      return () => {
        domRef.current?.removeEventListener('touchmove', preDefault);
      };
    }, [domRef.current]);

    return (
      <div className={$c('whiteboard-component', className || '')} {...otherProps} ref={domRef}>
        <div ref={containerRef} className={$c('whiteboard-container')} />
        {/*  TODO ipad 只读，后续需要编辑时去除 ipad 判断 */}
        {/* {state === 'done' && !isMobile && !isiOSApp && !isAndroidApp && !isPad && ( */}
        {state === 'done' && !isMobile && !isiOSApp && !isAndroidApp && (
          <>
            {ToolbarLeftTop}
            {ToolbarRightTop}
            {ToolbarLeft}
            {ModalClose}
            {ModalYouAreCurrentlyInPerspectiveSynchronization}
          </>
        )}
      </div>
    );
  }
);

export default Whiteboard;
